import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

const STORAGE_KEY = 'kae_cookie_consent_v1';
// 13 months in milliseconds (CNIL recommendation)
const CONSENT_TTL_MS = 13 * 30 * 24 * 60 * 60 * 1000;

const GTAG_ID = 'AW-586557208';

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.date) return null;
    if (Date.now() - parsed.date > CONSENT_TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ value, date: Date.now() }));
  } catch {
    /* noop */
  }
}

// Charge dynamiquement le script Google Ads (gtag.js) et appelle `gtag('config', ...)`.
// Idempotent : ne re-charge pas le script s'il est déjà présent.
function loadGtag() {
  if (typeof window === 'undefined') return;
  if (window.__gtagLoaded) return;
  window.__gtagLoaded = true;

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments); // eslint-disable-line prefer-rest-params
  }
  window.gtag = window.gtag || gtag;
  window.gtag('js', new Date());
  window.gtag('config', GTAG_ID);
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Si l'utilisateur a déjà accepté dans une session précédente, on charge gtag immédiatement
    const existing = readConsent();
    if (existing && existing.value === 'accepted') {
      loadGtag();
    }
    // Puis on gère l'affichage du bandeau
    const t = setTimeout(() => {
      if (!readConsent()) setVisible(true);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  const updateConsent = (v) => {
    if (typeof window.gtag !== 'function') return;
    window.gtag('consent', 'update', {
      ad_storage: v, ad_user_data: v, ad_personalization: v, analytics_storage: v,
    });
    window.dataLayer.push({ event: v === 'granted' ? 'cookie_consent_granted' : 'cookie_consent_denied' });
  };

  const handleAccept = () => {
    writeConsent('accepted');
    updateConsent('granted');
    loadGtag();
    setVisible(false);
  };

  const handleReject = () => {
    writeConsent('rejected');
    updateConsent('denied');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Bandeau de consentement aux cookies"
      data-testid="cookie-banner"
      className="fixed inset-x-3 bottom-3 z-[70] sm:inset-auto sm:right-5 sm:bottom-5 sm:max-w-md animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <div className="relative rounded-2xl bg-white/95 backdrop-blur-md border border-sky-100 shadow-[0_20px_50px_-10px_rgba(2,132,199,0.25)] p-5 sm:p-6">
        <button
          type="button"
          onClick={handleReject}
          data-testid="cookie-banner-close"
          aria-label="Refuser et fermer"
          className="absolute top-3 right-3 w-8 h-8 rounded-full text-slate-400 hover:text-sky-900 hover:bg-sky-50 flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center flex-shrink-0 shadow-sm">
            <Cookie className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-display font-bold text-sky-950 text-base leading-tight">Vos données, votre choix</h3>
            <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
              Ce site utilise des cookies techniques nécessaires à son fonctionnement, et — avec votre accord — une balise Google Ads
              pour mesurer l'efficacité de nos campagnes de soutien.{' '}
              <Link to="/politique-cookies" className="text-sky-700 underline underline-offset-2 hover:text-emerald-600 font-medium">
                En savoir plus
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:pl-[3.25rem]">
          <button
            type="button"
            onClick={handleReject}
            data-testid="cookie-banner-reject"
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-sky-800 bg-sky-50 hover:bg-sky-100 border border-sky-100 transition-colors"
          >
            Refuser
          </button>
          <button
            type="button"
            onClick={handleAccept}
            data-testid="cookie-banner-accept"
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-br from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 shadow-sm transition-all"
          >
            Accepter
          </button>
        </div>
      </div>
    </div>
  );
}
